<template>
  <b-modal
    ref="modalRef"
    size="lg"
    centered
    :title="isEdit ? 'Edit award budget' : 'Add award budget'"
    @hidden="onHidden"
  >
    <ValidationObserver ref="observerRef" v-slot="{ handleSubmit }">
      <b-form id="award-budget-form" @submit.prevent="handleSubmit(onSubmit)">
        <div v-if="budget" class="award-budget-modal__usage">
          <div class="award-budget-modal__usage-headline">
            {{ formatAmount(budget.spent_minor) }} of {{ formatAmount(budget.amount_minor) }} spent this
            {{ budgetPeriodNoun(budget.budget_period) }}
          </div>
          <budget-usage-bar :budget="budget" />
          <div class="award-budget-modal__usage-meta">
            <span v-if="budget.period_end_at">Renews {{ formatDate(budget.period_end_at) }}</span>
            <span>Spent {{ formatAmount(budget.spent_minor) }}</span>
            <span>Remaining {{ formatAmount(budget.remaining_minor) }}</span>
          </div>
        </div>

        <app-input
          v-if="isEdit"
          :value="budget?.award?.program_name || budget?.name"
          name="Award"
          label="Award"
          plaintext
        />
        <app-input
          v-else
          v-model="programId"
          :validate-immediate="false"
          name="Award"
          label="Award"
          type="dropdown"
          :options="awardOptions"
          undefined-label="Select a nominated award"
          rules="required"
        />

        <budget-amount-fields
          :amount="amount"
          :period="period"
          :currency-symbol="currencySymbol"
          period-helper="Period changes apply from the next renewal."
          @update:amount="amount = $event"
          @update:period="period = $event"
        />

        <small v-if="errorMessage" class="text-danger d-block">{{ errorMessage }}</small>
      </b-form>
    </ValidationObserver>

    <template #modal-footer>
      <b-button type="submit" variant="primary" form="award-budget-form" :disabled="saving">
        <b-spinner v-if="saving" small class="mr-1" />
        Save changes
      </b-button>
    </template>
  </b-modal>
</template>

<script setup>
import { computed, ref } from 'vue';
import { ValidationObserver } from 'vee-validate';
import { useMutation } from '@tanstack/vue-query';
import AppInput from '@/components/AppInput.vue';
import BudgetAmountFields from '@/pages/recognition/components/budgets/BudgetAmountFields.vue';
import BudgetUsageBar from '@/pages/recognition/components/budgets/BudgetUsageBar.vue';
import { createAwardBudget, updateRecognitionBudget } from '@/queries/recognition';
import { budgetPeriodNoun, majorToMinor, minorToMajorString } from '@/pages/recognition/utils/budgets';
import { toCurrency } from '@/utils';
import { toFormattedDate } from '@/utils/date';
import { currencySymbolFromCode } from '@/utils/common';
import useToast from '@/composables/useToast';
import State from '@/state/State';

const props = defineProps({
  availableAwards: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['saved']);

const { doToast, TOAST_VARIANT } = useToast();
const updateBudgetMutation = useMutation(updateRecognitionBudget());
const createAwardBudgetMutation = useMutation(createAwardBudget());

const modalRef = ref(null);
const observerRef = ref(null);
const budget = ref(null);
const programId = ref(undefined);
const amount = ref('');
const period = ref(undefined);
const saving = ref(false);
const errorMessage = ref('');

const isEdit = computed(() => Boolean(budget.value));
const awardOptions = computed(() =>
  props.availableAwards.map(award => ({ text: award.program_name, value: award.recognition_award_program_id })),
);
const currency = computed(() => State.state?.company?.default_currency);
const currencySymbol = computed(() => currencySymbolFromCode(currency.value));

const formatAmount = minor => toCurrency(minor ?? 0, currency.value);
const formatDate = date => toFormattedDate(date, 'D MMM YYYY');

const show = (awardBudget = null) => {
  budget.value = awardBudget;
  programId.value = undefined;
  amount.value = minorToMajorString(awardBudget?.amount_minor);
  period.value = awardBudget?.budget_period;
  errorMessage.value = '';
  modalRef.value?.show();
};

const onHidden = () => {
  budget.value = null;
  observerRef.value?.reset();
};

const onSubmit = async () => {
  const payload = { amount_minor: majorToMinor(amount.value), budget_period: period.value };
  saving.value = true;
  errorMessage.value = '';
  try {
    if (budget.value?.recognition_budget_id) {
      await updateBudgetMutation.mutateAsync({ budgetId: budget.value.recognition_budget_id, payload });
    } else {
      const awardId = budget.value?.award?.recognition_award_program_id || programId.value;
      await createAwardBudgetMutation.mutateAsync({ programId: awardId, payload });
    }
    doToast('Success', isEdit.value ? 'Award budget updated' : 'Award budget added', TOAST_VARIANT.SUCCESS);
    emit('saved');
    modalRef.value?.hide();
  } catch (e) {
    errorMessage.value =
      e?.variables?.message || e?.response?.data?.message || e?.message || 'Failed to update award budget';
  } finally {
    saving.value = false;
  }
};

defineExpose({ show });
</script>

<style scoped lang="scss">
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

:deep(.modal-footer) {
  border-top: 1px solid var(--palette-color-default-lighten-90);
}

.award-budget-modal {
  &__usage {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.875rem 1rem;
    margin-bottom: 1.25rem;
    border-radius: 0.5rem;
    background-color: var(--palette-color-default-lighten-95);
  }

  &__usage-headline {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--palette-color-default);
  }

  &__usage-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1rem;
    font-size: 0.8125rem;
    color: var(--palette-color-default-lighten-30);
  }
}
</style>
